import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const PAGE_SIZE = 50;

const LOG_TYPE_OPTIONS = [
    { value: 'AUTHENTICATION', label: 'Authentication' },
    { value: 'ADMIN',          label: 'Admin Access' },
    { value: 'USER_MANAGEMENT', label: 'User Mgmt' },
    { value: 'COLLECTION',     label: 'Collection' },
    { value: 'SECURITY',       label: 'Security' },
    { value: 'SYSTEM',         label: 'System' },
];

const ALL_TYPES = LOG_TYPE_OPTIONS.map(t => t.value);

const getLogTypeBadge = (logType, logAction) => {
    switch (logType) {
        case 'AUTHENTICATION':
            return logAction && logAction.toLowerCase().includes('successful')
                ? 'text-green-700 bg-green-100'
                : 'text-yellow-700 bg-yellow-100';
        case 'ADMIN':
            return 'text-red-700 bg-red-100';
        case 'USER_MANAGEMENT':
            return 'text-blue-700 bg-blue-100';
        case 'COLLECTION':
            return 'text-purple-700 bg-purple-100';
        case 'SECURITY':
            return 'text-red-700 bg-red-100';
        case 'SYSTEM':
        default:
            return 'text-gray-600 bg-gray-100';
    }
};

const getLogTypeLabel = (logType) => {
    const found = LOG_TYPE_OPTIONS.find(t => t.value === logType);
    return found ? found.label : logType;
};

const Pagination = ({ currentPage, totalPages, onPageChange, isLoading }) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const delta = 2;
        const range = [];
        const rangeWithDots = [];

        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++
        ) {
            range.push(i);
        }

        if (currentPage - delta > 2) rangeWithDots.push(1, '...');
        else rangeWithDots.push(1);

        rangeWithDots.push(...range);

        if (currentPage + delta < totalPages - 1) rangeWithDots.push('...', totalPages);
        else rangeWithDots.push(totalPages);

        return rangeWithDots;
    };

    const btnBase =
        'inline-flex items-center px-3 py-1.5 text-sm font-medium rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
    const btnActive =
        'bg-red-600 border-red-600 text-white';
    const btnInactive =
        'bg-white border-gray-300 text-gray-700 hover:bg-gray-50';

    return (
        <div className="flex items-center justify-center gap-1 mt-6 flex-wrap">
            <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className={`${btnBase} ${btnInactive}`}
                aria-label="First page"
            >
                «
            </button>
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className={`${btnBase} ${btnInactive}`}
                aria-label="Previous page"
            >
                ‹ Prev
            </button>

            {getPageNumbers().map((page, idx) =>
                page === '...' ? (
                    <span key={`dots-${idx}`} className="px-2 py-1.5 text-sm text-gray-500">
                        …
                    </span>
                ) : (
                    <button
                        key={page}
                        onClick={() => onPageChange(page)}
                        disabled={isLoading}
                        className={`${btnBase} ${page === currentPage ? btnActive : btnInactive}`}
                    >
                        {page}
                    </button>
                )
            )}

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className={`${btnBase} ${btnInactive}`}
                aria-label="Next page"
            >
                Next ›
            </button>
            <button
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className={`${btnBase} ${btnInactive}`}
                aria-label="Last page"
            >
                »
            </button>
        </div>
    );
};

const AdminLogs = () => {
    const { user, isLoading: authLoading, checkAdminStatus } = useAuth();
    const navigate = useNavigate();
    useDocumentTitle('Admin Logs');
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState({
        registrations_30d: 0,
        registrations_today: 0,
        blog_views_today: 0,
        active_sessions: 0,
    });
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);

    // Checkbox state: which types are currently VISIBLE (all checked by default)
    const [activeTypes, setActiveTypes] = useState(new Set(ALL_TYPES));
    const [pendingTypes, setPendingTypes] = useState(new Set(ALL_TYPES));

    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        search: '',
        log_types: '', // comma-separated active types sent to API
    });
    const [pendingFilters, setPendingFilters] = useState({
        date_from: '',
        date_to: '',
        search: '',
    });

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    useEffect(() => {
        const verifyAdminAccess = async () => {
            if (authLoading) return;

            if (!user) {
                navigate('/');
                return;
            }

            const isAdmin = await checkAdminStatus();
            if (!isAdmin) {
                navigate('/');
                return;
            }

            setIsAuthorized(true);
            setIsLoading(false);
        };

        verifyAdminAccess();
    }, [user, authLoading, checkAdminStatus, navigate]);

    useEffect(() => {
        if (!isAuthorized) return;
        fetchLogs(currentPage, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthorized, currentPage, filters]);

    const fetchLogs = async (page, activeFilters) => {
        setIsLoadingLogs(true);
        try {
            const offset = (page - 1) * PAGE_SIZE;
            const params = new URLSearchParams({
                limit: PAGE_SIZE.toString(),
                offset: offset.toString()
            });

            if (activeFilters.log_types) params.append('log_types', activeFilters.log_types);
            if (activeFilters.date_from) params.append('date_from', activeFilters.date_from);
            if (activeFilters.date_to) params.append('date_to', activeFilters.date_to);
            if (activeFilters.search) params.append('search', activeFilters.search);

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin_get_logs.php?${params}`,
                { credentials: 'include' }
            );

            const data = await response.json();

            if (data.success) {
                setLogs(data.logs);
                setTotalCount(data.pagination.total_count);
                setStats(data.stats);
            } else {
                console.error('Error loading logs:', data.error);
                if (data.error === 'Admin access required' || data.error === 'Not authenticated') {
                    navigate('/');
                }
            }
        } catch (error) {
            console.error('Error loading logs:', error);
        } finally {
            setIsLoadingLogs(false);
        }
    };

    const toggleType = (typeValue) => {
        setPendingTypes(prev => {
            const next = new Set(prev);
            if (next.has(typeValue)) {
                next.delete(typeValue);
            } else {
                next.add(typeValue);
            }
            return next;
        });
    };

    const handleApplyFilters = () => {
        setActiveTypes(new Set(pendingTypes));
        const logTypesParam = pendingTypes.size === ALL_TYPES.length
            ? '' // all selected → no filter needed
            : [...pendingTypes].join(',');
        setFilters({
            ...pendingFilters,
            log_types: logTypesParam,
        });
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        const allSet = new Set(ALL_TYPES);
        setPendingTypes(allSet);
        setActiveTypes(allSet);
        const empty = { date_from: '', date_to: '', search: '', log_types: '' };
        setPendingFilters({ date_from: '', date_to: '', search: '' });
        setFilters(empty);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const formatTimestamp = (timestamp) =>
        new Date(timestamp).toLocaleString();

    const hasActiveFilters =
        filters.log_types || filters.date_from || filters.date_to || filters.search;

    if (isLoading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Verifying admin access...</p>
                </div>
            </div>
        );
    }

    if (!isAuthorized) return null;

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Breadcrumb */}
                <nav className="flex mb-6" aria-label="Breadcrumb">
                    <ol className="inline-flex items-center space-x-1 md:space-x-2">
                        <li className="inline-flex items-center">
                            <Link
                                to="/admin"
                                className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-red-600"
                            >
                                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                </svg>
                                Admin
                            </Link>
                        </li>
                        <li>
                            <div className="flex items-center">
                                <svg className="w-4 h-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm font-medium text-gray-500">System Logs</span>
                            </div>
                        </li>
                    </ol>
                </nav>

                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">System Logs</h1>
                    <p className="mt-1 text-gray-500">
                        Monitor activity, authentication events, and security incidents.
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-6">
                    {[
                        { label: 'Registrations (30d)',  value: stats.registrations_30d  ?? 0 },
                        { label: 'Registrations Today',  value: stats.registrations_today ?? 0 },
                        { label: 'Blog Views Today',     value: stats.blog_views_today   ?? 0 },
                        { label: 'Active Sessions (1h)', value: stats.active_sessions    ?? 0 },
                    ].map((stat) => (
                        <div key={stat.label} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                            <p className="text-xs font-medium text-gray-500 truncate">{stat.label}</p>
                            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value.toLocaleString()}</p>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
                    <div className="px-5 py-4 border-b border-gray-100">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Filters</h2>
                    </div>
                    <div className="px-5 py-4 space-y-4">

                        {/* Log type checkboxes */}
                        <div>
                            <p className="block text-sm font-medium text-gray-700 mb-2">Log Types</p>
                            <div className="flex flex-wrap gap-2">
                                {LOG_TYPE_OPTIONS.map(t => {
                                    const checked = pendingTypes.has(t.value);
                                    return (
                                        <label
                                            key={t.value}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium cursor-pointer select-none transition-colors ${
                                                checked
                                                    ? 'bg-red-50 border-red-300 text-red-700'
                                                    : 'bg-gray-50 border-gray-200 text-gray-400 line-through'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="sr-only"
                                                checked={checked}
                                                onChange={() => toggleType(t.value)}
                                            />
                                            {t.label}
                                        </label>
                                    );
                                })}
                                <button
                                    type="button"
                                    onClick={() => setPendingTypes(new Set(ALL_TYPES))}
                                    className="text-xs text-gray-400 hover:text-red-600 underline ml-1"
                                >
                                    all
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPendingTypes(new Set())}
                                    className="text-xs text-gray-400 hover:text-red-600 underline"
                                >
                                    none
                                </button>
                            </div>
                        </div>

                        {/* Date + search row */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div>
                                <label htmlFor="log-search" className="block text-sm font-medium text-gray-700 mb-1">
                                    Search Action
                                </label>
                                <input
                                    type="text"
                                    id="log-search"
                                    value={pendingFilters.search}
                                    onChange={(e) =>
                                        setPendingFilters(prev => ({ ...prev, search: e.target.value }))
                                    }
                                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                                    placeholder="Search log messages…"
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                />
                            </div>

                            <div>
                                <label htmlFor="date-from" className="block text-sm font-medium text-gray-700 mb-1">
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    id="date-from"
                                    value={pendingFilters.date_from}
                                    onChange={(e) =>
                                        setPendingFilters(prev => ({ ...prev, date_from: e.target.value }))
                                    }
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                />
                            </div>

                            <div>
                                <label htmlFor="date-to" className="block text-sm font-medium text-gray-700 mb-1">
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    id="date-to"
                                    value={pendingFilters.date_to}
                                    onChange={(e) =>
                                        setPendingFilters(prev => ({ ...prev, date_to: e.target.value }))
                                    }
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-1">
                            <button
                                onClick={handleApplyFilters}
                                disabled={isLoadingLogs}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                            >
                                <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                                </svg>
                                Apply Filters
                            </button>
                            {hasActiveFilters && (
                                <button
                                    onClick={handleClearFilters}
                                    disabled={isLoadingLogs}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Log Table */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                            Activity Log
                        </h2>
                        <span className="text-sm text-gray-500">
                            {isLoadingLogs ? (
                                'Loading…'
                            ) : (
                                <>
                                    {totalCount === 0
                                        ? 'No results'
                                        : `${((currentPage - 1) * PAGE_SIZE) + 1}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount.toLocaleString()}`}
                                    {hasActiveFilters && (
                                        <span className="ml-2 text-red-600 font-medium">(filtered)</span>
                                    )}
                                </>
                            )}
                        </span>
                    </div>

                    {isLoadingLogs ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-16">
                            <svg className="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-sm font-medium text-gray-700">No logs found</p>
                            <p className="mt-1 text-sm text-gray-500">Try adjusting your filters.</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden sm:table-cell">
                                                Timestamp
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Type
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                User
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Action
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                                                IP Address
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {logs.map((log) => (
                                            <tr key={log.log_id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 hidden sm:table-cell">
                                                    {formatTimestamp(log.log_timestamp)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getLogTypeBadge(log.log_type, log.log_action)}`}>
                                                        {getLogTypeLabel(log.log_type)}
                                                    </span>
                                                    {/* Show timestamp below badge on mobile */}
                                                    <div className="text-xs text-gray-400 mt-0.5 sm:hidden">
                                                        {formatTimestamp(log.log_timestamp)}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {log.username || (log.log_user ? `#${log.log_user}` : <span className="text-gray-400">Anon</span>)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700 max-w-xs sm:max-w-md break-words">
                                                    {log.log_action}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono hidden md:table-cell">
                                                    {log.log_ip || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="px-5 py-4 border-t border-gray-100">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                    isLoading={isLoadingLogs}
                                />
                                <p className="text-center text-xs text-gray-400 mt-2">
                                    Page {currentPage} of {totalPages} &mdash; {totalCount.toLocaleString()} total entries
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminLogs;
